/* eslint-disable no-nested-ternary */
import {
  Alert, Box, CircularProgress, Container, Link, Snackbar, TextField, Typography, useTheme,
} from '@mui/material';
import {
  blueGrey, common, grey, red,
} from '@mui/material/colors';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { generateMagicLinkLoginSession } from '../../api/loginAPI';
import { useLogo } from '../../config/useLogo';
import {
  emailRegex,
  ImproveIt360SolutionOrgName,
} from '../../common/constants';
import { TestIds } from '../../assets/testIds';
import improveIt360Logo from '../../assets/images/improve_it_360_logo.svg';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { Logger } from '../../config/logging/logger';
import RecaptchaV2 from '../../common/RecaptchaV2';
import RecaptchaV3 from '../../common/RecaptchaV3';
import OptimusButton from '../../common/library/Button/Button';
import { useOptimizelyFlag } from '../../common/hooks/useOptimizelyFlag';
import {
  PublicAccessFlag, RecaptchaFlag,
} from '../../common/OptimizelyFlagsConstants';
import { LoginSessionResponse } from '../../common/models/AuthenticationRequest';
import { RecaptchaActions } from '../../common/enums';
import { SERVICE_NATION_BY_EVER_PRO_DOMAIN } from '../../config/domains';

interface IFormInputs {
  email: string;
  loginCodes: {
    value: string;
  }[]
}

export function Login() {
  const {
    handleSubmit,
    setValue,
    control,
    formState,
    getValues,
    clearErrors,
  } = useForm<IFormInputs>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const [isLogging, setIsLogging] = useState(false);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState<string | undefined>(undefined);
  const [isV2TokenValid, setIsV2TokenValid] =
  useState<boolean | null>(null);
  const [isV3TokenValid, setIsV3TokenValid] =
  useState<boolean | null>(null);

  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);

  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialized = useRef(false);
  const theme = useTheme();
  const { secondaryOne: Logo } = useLogo();
  const solutionOrg = searchParams.get('solutionOrg');
  const { solutionOrgName, domainName } = useSolutionOrg();
  const { trackLogin, trackSignUp } = useCustomEventTracking();
  const logger = useMemo(() => Logger.create('Login'), []);

  const isImproveIt360 = solutionOrgName === ImproveIt360SolutionOrgName;

  // get component based on optimizely decision
  const { decision, isDecisionReady } = useOptimizelyFlag({
    flag: PublicAccessFlag,
  });
  const {
    decision: recaptchaDecision,
    isDecisionReady: isRecaptchaDecisionReady,
  } = useOptimizelyFlag({
    flag: RecaptchaFlag,
  });
  // Submission guard to prevent duplicate concurrent submissions
  const isSubmittingRef = useRef<boolean>(false);

  const isCTADisabled = isLogging ||
      // disable the login button if the user fails the challenge based reCAPTCHA
      (isV2TokenValid === false) ||
      // disable the login button if the user fails the invisible based reCAPTCHA
      // and has not completed the visible reCAPTCHA yet
      (isV3TokenValid === false && !recaptchaV2Token);

  useEffect(() => {
    const testSentry = searchParams.get('testSentry');

    if (testSentry === 'true' && process.env.REACT_APP_ENVIRONMENT !== 'prod') {
      try {
        throw new Error('Sentry testing error');
      } catch (error) {
        logger.error(Logger.format('Sentry testing error', error));
        Sentry.captureException(error);
      }
    }
  }, [searchParams, logger]);

  // ============================================================
  // MAGIC LINK FLOW - Send email with clickable link
  // ============================================================
  const handleMagicLinkFlow = async (email: string) => {
    try {
      let recaptchaV3Token: string | undefined;
      if (isRecaptchaDecisionReady && recaptchaDecision.enabled) {
        // get invisible reCAPTCHA token
        if (isV3TokenValid === null) {
          recaptchaV3Token = await new Promise((resolve) => {
            window.grecaptcha?.enterprise.ready(async () => {
              const token = await window.grecaptcha?.enterprise.execute(
                process.env.REACT_APP_GOOGLE_RECAPTCHA_V3_SITE_KEY || '',
                { action: RecaptchaActions.Login },
              );
              resolve(token as string);
            });
          });
        }
      }

      // Request magic link login session
      const loginSessionResponse: LoginSessionResponse =
        await generateMagicLinkLoginSession(
          email,
          recaptchaV2Token,
          recaptchaV3Token,
          searchParams.get('afterLoginPath') ?? undefined,
        );

      if (isRecaptchaDecisionReady && recaptchaDecision.enabled) {
        if (loginSessionResponse?.isV3TokenValid === false) {
          setIsV3TokenValid(false);
          return;
        }
        if (loginSessionResponse?.isV2TokenValid === false) {
          setIsV2TokenValid(false);
          return;
        }
      }

      setMagicLinkEmail(email);
      setMagicLinkSent(true);
      trackLogin('Auth', 'MagicLink', 'Member');
    } catch (err) {
      setOpenSuccessSnackbar(false);
      setOpenErrorSnackbar(true);
      logger.error(Logger.format(err));
    } finally {
      setIsLogging(false);
    }
  };

  const onSubmit = handleSubmit(async (data: IFormInputs) => {
    if (data.email) {
      // Prevent duplicate concurrent submissions
      if (isSubmittingRef.current) {
        return;
      }

      try {
        isSubmittingRef.current = true;
        setIsLogging(true);
        // MAGIC LINK FLOW: Send email with link
        await handleMagicLinkFlow(data.email);
      } finally {
        isSubmittingRef.current = false;
      }
    }
  });

  // handle email prefill
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      const email = searchParams.get('email');
      if (email) {
        setValue('email', email);
        onSubmit();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  const openContactSupportForm = () => {
    if (solutionOrg) {
      navigate(`/contact-support?solutionOrg=${solutionOrg}`);
    } else {
      navigate('/contact-support');
    }
  };

  const openSignUpPage = (email?: string) => {
    trackSignUp('Auth', 'Create', 'Member');
    if (!email) {
      navigate('/signup');
    } else {
      navigate(`/signup?email=${encodeURIComponent(email)}`);
    }
  };

  return (
    <Container
      maxWidth='sm'
      sx={{
        background: common.white,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100vh',
        pr: theme.spacing(3),
        pl: theme.spacing(3),
      }}
    >
      <>
        <Snackbar
          open={openSuccessSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('success.login')}
          </Alert>
        </Snackbar>
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={2000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('error.generalMessage')}
          </Alert>
        </Snackbar>
        <Box sx={{ textAlign: 'left' }}>
          {Logo && <img src={Logo as string} alt="Logo" style={{ width: theme.spacing(25) }} />}
          {isImproveIt360 && (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', mt: 1, mb: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ mr: 1, color: grey[600] }}
              >
                {t('login.broughtToYouBy')}
              </Typography>
              <img
                src={improveIt360Logo}
                alt="Improve It 360 Logo"
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            marginTop: theme.spacing(4),
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              mb: theme.spacing(1),
            }}
            variant='h4'>{t('login.heading')}</Typography>
          <Box
            sx={{ marginTop: theme.spacing(3) }}
          >
            <form onSubmit={onSubmit}>
              {magicLinkSent ? (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    component="p"
                    variant='bodyLg'
                    sx={{
                      color: grey[600],
                      textAlign: 'left',
                    }}

                  >
                    <Trans
                      i18nKey="login.loginMagicLinkSent"
                      values={{ email: magicLinkEmail }}
                      components={{
                        boldEmail: <strong />,
                        break: <br />,
                      }}
                    />
                  </Typography>
                  <Typography
                    component="p"
                    variant='bodyMd'
                    sx={{
                      color: grey[600],
                      textAlign: 'left',
                      mt: theme.spacing(2),
                    }}
                  >
                    <Trans
                      i18nKey="login.loginMagicLinkNotSent"
                      components={{
                        contact: (
                          <a
                            href="/contact-support"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {/* Fallback text, will be replaced by translation,
                              only used to fix eslint issue */}
                              contact support
                          </a>
                        ),
                      }}
                    />
                  </Typography>
                  <OptimusButton
                    size='m'
                    customVariant='secondaryGray'
                    fullWidth
                    sx={{ mt: theme.spacing(3) }}
                    onClick={() => {
                      setMagicLinkSent(false);
                      setMagicLinkEmail('');
                      setValue('email', '');
                    }}
                    buttonText={t('login.editEmailAddress') || 'Try a different email'}
                  />
                </Box>
              ) : (
                <>
                  <Box textAlign={'left'} mb={theme.spacing(0.75)}>
                    <Typography variant='body7'>{t('login.emailInputLabel')}</Typography>
                  </Box>
                  <Box>
                    <Controller
                      name="email"
                      control={control}
                      defaultValue={''}
                      render={({
                        field: {
                          onChange, onBlur, value,
                        },
                      }) => <TextField
                        value={value} // Set the value directly
                        onChange={(event) => {
                          onChange(event.target.value.trim());
                          setMagicLinkSent(false);
                          setLoginError('');
                          if (formState?.errors?.email) clearErrors('email');
                        }}
                        onBlur={onBlur}
                        placeholder={t('login.emailPlaceholderText') as string}
                        data-testid={TestIds.login.emailTxt}
                        InputProps={{
                          sx: {
                            borderRadius: theme.spacing(1),
                            border: `1px solid ${blueGrey[200]}`,
                            background: common.white,
                            fontWeight: '400',
                            fontSize: theme.spacing(2),
                            paddingLeft: theme.spacing(0),
                            '& .MuiInputBase-input': {
                              padding: '10px 14px',
                            },
                          },
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root.Mui-focused': {
                            '& > fieldset': {
                              border: `1px solid ${formState?.errors?.email && formState.errors.email.type !== 'required' ?
                                red[500] : theme.palette.brand.primary}`,
                            },
                          },
                        }}
                      />}
                      rules={{
                        required: 'Email is required',
                        pattern: {
                          value: emailRegex,
                          message: `${t('error.invalidLoginEmail')}`,
                        },
                      }}
                    />
                  </Box>
                </>
              )}
              <Box sx={{ minHeight: theme.spacing(4.5) }}>
                {(formState?.errors?.email) &&
                  <Alert
                    icon={false}
                    sx={{
                      background: common.white,
                      color: theme.palette.error.main,
                      p: 0,
                      textAlign: 'left',
                    }}
                    severity='error'>
                    {formState?.errors.email.message}
                  </Alert>}
                {loginError &&
                  <Alert
                    icon={false}
                    sx={{
                      background: common.white,
                      color: theme.palette.error.main,
                      p: 0,
                    }}
                    severity='error'
                  >
                    {loginError}
                  </Alert>}
              </Box>
              {!magicLinkSent && (
                <>
                  {isRecaptchaDecisionReady && recaptchaDecision.enabled && <>
                    {isV3TokenValid === null &&
                      <RecaptchaV3 />
                    }
                    {isV3TokenValid === false &&
                      <RecaptchaV2
                        onVerify={(token: string) => setRecaptchaV2Token(token)}
                        onExpired={() => setRecaptchaV2Token(undefined)}
                      />
                    }</>
                  }
                  <OptimusButton
                    data-testid={TestIds.login.loginBtn}
                    size={'m'}
                    customVariant='primary'
                    disabled={isCTADisabled}
                    type={'submit'}
                    buttonText={isLogging ?
                      <CircularProgress color="inherit" size={24} /> :
                      `${t('button.sendMagicLink')}`}
                  />
                  {isDecisionReady && decision.enabled &&
                    domainName === SERVICE_NATION_BY_EVER_PRO_DOMAIN &&
                    <OptimusButton
                      data-testid={TestIds.login.createAccountBtn}
                      size={'m'}
                      customVariant='secondaryGray'
                      sx={{
                        mb: theme.spacing(2),
                        mt: theme.spacing(3),
                      }}
                      disabled={isCTADisabled}
                      type="button"
                      onClick={() => openSignUpPage((getValues('email') || '').trim() || undefined)}
                      buttonText={t('button.createAccount') || ''}
                    />
                  }
                </>
              )}
            </form>
          </Box>
        </Box>
        {!magicLinkSent && (
          <Box
            sx={{
              mt: theme.spacing(3),
              textAlign: 'center',
            }}
          >
            <Typography variant='body7'>{t('login.loginTroubleMessage')} &nbsp;
              <Link onClick={() => openContactSupportForm()}
                sx={{
                  cursor: 'pointer',
                }}
                data-testid={TestIds.login.contactSupportLnk}
                variant='h6'>
                {t('login.contactSupport')}</Link>
            </Typography>
          </Box>
        )}
      </>
    </Container>
  );
}
